"use strict";
/**
 * Coordinate transformation utilities for PDF Document Editor
 *
 * Handles coordinate transformations between different coordinate systems:
 * - Screen coordinates: Mouse/touch event clientX/clientY (relative to viewport)
 * - Canvas coordinates: Coordinates relative to the canvas element (scaled)
 * - PDF coordinates: Unscaled coordinates for storage in annotations
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.screenToCanvas = screenToCanvas;
exports.canvasToPDF = canvasToPDF;
exports.pdfToCanvas = pdfToCanvas;
exports.screenToPDF = screenToPDF;
exports.getCanvasRelativePosition = getCanvasRelativePosition;
exports.isPointInCanvas = isPointInCanvas;
exports.constrainToCanvas = constrainToCanvas;
/**
 * Convert screen coordinates (from mouse event) to canvas coordinates
 *
 * @param screenX - clientX from mouse event
 * @param screenY - clientY from mouse event
 * @param canvasRect - DOMRect from canvas.getBoundingClientRect()
 * @returns Canvas coordinates {x, y}
 */
function screenToCanvas(screenX, screenY, canvasRect) {
    return {
        x: screenX - canvasRect.left,
        y: screenY - canvasRect.top,
    };
}
/**
 * Convert canvas coordinates (scaled) to PDF coordinates (unscaled)
 *
 * @param canvasX - X coordinate on canvas
 * @param canvasY - Y coordinate on canvas
 * @param scale - Current zoom/scale factor
 * @returns PDF coordinates {x, y}
 */
function canvasToPDF(canvasX, canvasY, scale) {
    return {
        x: canvasX / scale,
        y: canvasY / scale,
    };
}
/**
 * Convert PDF coordinates (unscaled) to canvas coordinates (scaled)
 *
 * @param pdfX - X coordinate in PDF space
 * @param pdfY - Y coordinate in PDF space
 * @param scale - Current zoom/scale factor
 * @returns Canvas coordinates {x, y}
 */
function pdfToCanvas(pdfX, pdfY, scale) {
    return {
        x: pdfX * scale,
        y: pdfY * scale,
    };
}
/**
 * Convert screen coordinates directly to PDF coordinates (combining screenToCanvas and canvasToPDF)
 *
 * @param screenX - clientX from mouse event
 * @param screenY - clientY from mouse event
 * @param canvasRect - DOMRect from canvas.getBoundingClientRect()
 * @param scale - Current zoom/scale factor
 * @returns PDF coordinates {x, y}
 */
function screenToPDF(screenX, screenY, canvasRect, scale) {
    var canvasCoords = screenToCanvas(screenX, screenY, canvasRect);
    return canvasToPDF(canvasCoords.x, canvasCoords.y, scale);
}
/**
 * Get canvas-relative position from a mouse or drag event
 * Convenience function that extracts coordinates from event and converts to PDF coordinates
 *
 * @param event - MouseEvent or DragEvent
 * @param canvasElement - The canvas HTML element
 * @param scale - Current zoom/scale factor
 * @returns PDF coordinates {x, y} or null if canvas element is invalid
 */
function getCanvasRelativePosition(event, canvasElement, scale) {
    if (!canvasElement) {
        return null;
    }
    var canvasRect = canvasElement.getBoundingClientRect();
    return screenToPDF(event.clientX, event.clientY, canvasRect, scale);
}
/**
 * Check if a point is within a canvas element
 *
 * @param screenX - clientX from mouse event
 * @param screenY - clientY from mouse event
 * @param canvasRect - DOMRect from canvas.getBoundingClientRect()
 * @returns true if point is within canvas bounds
 */
function isPointInCanvas(screenX, screenY, canvasRect) {
    var canvasCoords = screenToCanvas(screenX, screenY, canvasRect);
    return (canvasCoords.x >= 0 &&
        canvasCoords.x <= canvasRect.width &&
        canvasCoords.y >= 0 &&
        canvasCoords.y <= canvasRect.height);
}
/**
 * Constrain position to be within canvas bounds
 * Ensures that a field doesn't get placed outside the visible PDF area
 *
 * @param position - Position object with x, y, width, height
 * @param canvasWidth - Width of the canvas in PDF coordinates
 * @param canvasHeight - Height of the canvas in PDF coordinates
 * @returns Constrained position
 */
function constrainToCanvas(position, canvasWidth, canvasHeight) {
    return {
        x: Math.max(0, Math.min(position.x, canvasWidth - position.width)),
        y: Math.max(0, Math.min(position.y, canvasHeight - position.height)),
        width: position.width,
        height: position.height,
    };
}
